import * as React from "react";
import { useForm } from "react-hook-form";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import InfoIcon from "@mui/icons-material/Info";
import { grey } from "@mui/material/colors";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import { useSelector, useDispatch } from "react-redux";
import configData from "../../../config.js";
import { withSnackbar } from "../../../components/SnackbarHOC";
import {
  handleCommonError,
  ENTITY_TYPES,
  NOTE_TYPE_VALUE,
  OVERRIDE_TYPE,
  MenuProps,
  DropdownSx,
  prettyNumericRound,
  getFormattedNumber,
} from "../../../utils";
import {
  calculateAvailableCreditLimit,
  setAdjQtyError,
  setPrevAdjQty,
} from "../../../store/globalSlice";
import { useGetNotesForPlanogramQuery } from "../../../services/notesApi";
import useRequest from "../../../hooks/useRequest";
import useStableCallback from "../../../hooks/useStableCallback";
import { isFeatureActive } from "../../../utils/featureFlag";

export const groupBy = [
  "Department",
  "Category",
  "Planogram",
  "Planogram #",
  "SKU",
  "Vendor",
];

export const groupMapping = {
  Category: "Class",
  Planogram: "Sub Class",
  "Planogram #": "Planogram",
};

const getSearchGroupMapping = (currentSearch) => {
  const mappedValue = groupMapping[currentSearch];
  return !!mappedValue ? groupMapping[currentSearch] : currentSearch;
};

const getCreditCostValue = (value) => {
  const updatedValue = prettyNumericRound(value || 0);
  return `$${getFormattedNumber(updatedValue)}`;
};

function DiscountSearch({
  disc,
  selectToggle = () => {},
  getFilters,
  getFilterValues = () => {},
  getData,
  snackbarShowMessage,
  entityType = ENTITY_TYPES.discount,
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const { requestMaker } = useRequest();
  const { promiseInProgress } = usePromiseTracker();
  const [deptName, setDeptName] = React.useState([]);
  const [clsName, setClsName] = React.useState([]);
  const [subName, setSubName] = React.useState([]);
  const [applyDisc, setApplyDisc] = React.useState(groupBy[0]);
  const [depmt, setDepmt] = React.useState("");
  const [deptNameError, setDeptNameError] = React.useState("");
  const [vendorNameError, setVendorNameError] = React.useState("");
  const [cls, setCls] = React.useState("");
  const [subcls, setSubcls] = React.useState("");
  const [selected, setSelected] = React.useState(false);
  const store = useSelector((store) => store.global.store);
  const dispatch = useDispatch();

  useGetNotesForPlanogramQuery(
    {
      noteType: NOTE_TYPE_VALUE.planner,
      storeId: store.id,
    },
    { skip: [ENTITY_TYPES.discount, ENTITY_TYPES.orders].includes(entityType) }
  );

  useGetNotesForPlanogramQuery(
    {
      noteType: NOTE_TYPE_VALUE.retailer,
      storeId: store.id,
    },
    { skip: [ENTITY_TYPES.discount, ENTITY_TYPES.orders].includes(entityType) }
  );

  const restrictAlpha = (e) => {
    let key = e.key;
    if (
      (parseInt(key) >= 0 && parseInt(key) <= 9) ||
      key === "Backspace" ||
      key === "Control" ||
      key === "c" ||
      key === "x" ||
      key === "v" ||
      key === "Copy" ||
      key === "Cut" ||
      key === "Paste" ||
      key === "Tab"
    ) {
    } else {
      e.preventDefault();
    }
  };

  React.useEffect(() => {
    setDepmt("");
    setCls("");
    setSubcls("");
    reset({ planogram: "", sku: "" });
  }, [applyDisc]);

  React.useEffect(() => {
    selectToggle(selected);
  }, [selected, selectToggle]);

  React.useEffect(() => {
    requestMaker({ method: "get", url: configData.GetAllDepartment }).then(
      (res) => {
        setDeptName(res.data);
      }
    );
    requestMaker({
      method: "post",
      url: configData.GetAvailableCreditLimit,
      data: { storeId: store?.id },
    }).then((res) => {
      const totalCost = res?.data?.totalCost ?? 0;
      dispatch(calculateAvailableCreditLimit(totalCost));
    });
  }, [requestMaker, dispatch, store.id]);

  React.useEffect(() => {
    if (deptName.length > 0 && depmt !== "") {
      requestMaker({
        method: "post",
        url: configData.GetClassByDepartment,
        data: {
          departmentName: depmt,
        },
      }).then((res) => {
        setClsName(res.data);
      });
    } else {
      setClsName([]);
    }
  }, [deptName.length, depmt]);

  React.useEffect(() => {
    if (clsName.length > 0 && cls !== "") {
      requestMaker({
        method: "post",
        url: configData.GetSubClassByClass,
        data: {
          className: cls,
        },
      }).then((res) => {
        setSubName(res.data);
      });
    } else {
      setSubName([]);
    }
  }, [clsName.length, cls]);

  const postDataURL = React.useMemo(() => {
    switch (entityType) {
      case ENTITY_TYPES.discount:
        return configData.GetAllDiscount;
      case ENTITY_TYPES.planogram:
        return configData.AllPlanogramItemsByFilterV2;
      case ENTITY_TYPES.orders:
        return configData.GetOrdersByFilter;
      default:
        return configData.GetAllDiscount;
    }
  }, [entityType]);

  const pogRequiredFields = [groupBy[1], groupBy[2], groupBy[3]].includes(
    applyDisc
  );

  const onSubmit = (data) => {
    // Validation for department field
    if (pogRequiredFields) {
      if (groupBy[3] === applyDisc) {
        if (!depmt && !cls && !subcls && !data?.planogram) {
          return setDeptNameError("This field is required.");
        }
      } else if (!depmt) {
        return setDeptNameError("This field is required.");
      }
    }
    setDeptNameError("");
    if (groupBy[5] === applyDisc) {
      if (!depmt && !cls && !subcls && !data?.vendor) {
        return setVendorNameError("This field is required.");
      }
    }
    setVendorNameError("");
    const filterArray = [depmt, cls, subcls];
    const keyNames = ["fl_dept_name", "fl_class_name", "fl_sub_name"];
    let filterList = [];
    for (let i = 0; i < filterArray.length; i++) {
      if (filterArray[i]) {
        filterList = [...filterList, keyNames[i]];
      }
    }
    getFilters(filterList);
    getFilterValues([
      depmt,
      cls,
      subcls,
      data?.planogram,
      data?.sku,
      data?.vendor,
    ]);
    getData({});

    const filters = {
      storeId: store.id,
      viewByPlanogram: getSearchGroupMapping(applyDisc),
      departmentName: depmt,
      className: cls,
      subClassName: subcls,
      planogramName: data?.planogram,
      sku: data.sku ? Number(data.sku) : null,
      vendorName: data?.vendor,
      selected: Number(selected),
      itemLevel: ["SKU", "Vendor"].includes(applyDisc)
        ? OVERRIDE_TYPE.itemLevel
        : OVERRIDE_TYPE.filterLevel,
    };
    return trackPromise(
      requestMaker({
        method: "post",
        url: postDataURL,
        data: filters,
      })
        .then((res) => {
          dispatch(setAdjQtyError(null));
          dispatch(setPrevAdjQty(null));
          disc(applyDisc);
          const details = typeof res?.data !== "object" ? {} : res?.data;
          getData(details);
          if (Object.keys(details).length === 0) {
            snackbarShowMessage("No details found for the given data.", "info");
          }
        })
        .catch((err) => {
          handleCommonError(snackbarShowMessage);
        })
    );
  };

  const onReset = useStableCallback(() => {
    setApplyDisc("Department");
    setDepmt("");
    setCls("");
    setSubcls("");
    setSelected(false);
    reset({ planogram: "", sku: "" });
    getData({});
    setDeptNameError("");
    setVendorNameError("");
    dispatch(setAdjQtyError(null));
    dispatch(setPrevAdjQty(null));
  });

  React.useEffect(() => {
    onReset();
  }, [entityType, onReset]);

  const applyEntityLabel = React.useMemo(() => {
    switch (entityType) {
      case ENTITY_TYPES.discount:
        return "Apply Discount By";
      case ENTITY_TYPES.planogram:
        return "View Planogram By";
      case ENTITY_TYPES.orders:
        return "View Order By";
      default:
        return "Apply Discount By";
    }
  }, [entityType]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
      <Paper
        sx={{
          display: "flex",
          justifyContent: "center",
          padding: 2,
        }}
      >
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={4} md={2.4} lg={2.4}>
            <FormControl size="small" fullWidth margin="dense">
              <InputLabel id="groupBy">{applyEntityLabel}</InputLabel>
              <Select
                labelId="groupBy"
                label={applyEntityLabel}
                value={applyDisc}
                onChange={(e) => {
                  setDeptNameError("");
                  setVendorNameError("");
                  setApplyDisc(e.target.value);
                }}
                MenuProps={MenuProps}
              >
                {groupBy.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={2.4} lg={2.4}>
            <FormControlLabel
              sx={{ my: 1, mx: 0 }}
              control={
                <Switch
                  checked={selected}
                  onChange={(e) => {
                    setSelected(e.target.checked);
                  }}
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
              label="Display Selected Only"
              labelPlacement="start"
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3.6} lg={3.6}>
            <Typography sx={{ my: 2 }}>
              OSO Credit Limit:{" "}
              {getCreditCostValue(!!store.creditLimit ? store.creditLimit : 0)}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={4}
            md={entityType !== ENTITY_TYPES.orders ? 3.6 : 3}
            lg={entityType !== ENTITY_TYPES.orders ? 3.6 : 3}
          >
            {isFeatureActive() && (
              <Typography sx={{ my: 2 }}>
                Available Credit Limit:{" "}
                {getCreditCostValue(
                  !!store.availableCreditLimit ? store.availableCreditLimit : 0
                )}
              </Typography>
            )}
          </Grid>
          {applyDisc !== groupBy[4] && (
            <Grid item xs={12} sm={4} md={2.4} lg={2.4}>
              <FormControl
                size="small"
                fullWidth
                margin="dense"
                required={[groupBy[1], groupBy[2]].includes(applyDisc)}
                error={!!deptNameError || !!vendorNameError}
              >
                <InputLabel id="dep">Department</InputLabel>
                <Select
                  autoWidth
                  labelId="dep"
                  label="Department"
                  value={depmt}
                  onChange={(e) => {
                    setVendorNameError("");
                    setDeptNameError("");
                    setDepmt(e.target.value);
                    setCls("");
                    setSubcls("");
                  }}
                  MenuProps={MenuProps}
                  required={[groupBy[1], groupBy[2]].includes(applyDisc)}
                >
                  <MenuItem value="">None</MenuItem>
                  {deptName.map((name) => (
                    <MenuItem
                      sx={DropdownSx}
                      key={name.DepartmentName}
                      value={name.DepartmentName}
                    >
                      {name.DepartmentName}
                    </MenuItem>
                  ))}
                </Select>
                {[groupBy[1], groupBy[2]].includes(applyDisc) && (
                  <FormHelperText error={!!deptNameError}>
                    {deptNameError}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          )}
          {![groupBy[0], groupBy[4]].includes(applyDisc) && (
            <Grid item xs={12} sm={4} md={2.4} lg={2.4}>
              <FormControl
                size="small"
                fullWidth
                margin="dense"
                error={
                  ([groupBy[3]].includes(applyDisc) && !!deptNameError) ||
                  ([groupBy[5]].includes(applyDisc) && !!vendorNameError)
                }
              >
                <InputLabel id="cls">Category</InputLabel>
                <Select
                  labelId="cls"
                  label="Category"
                  value={cls}
                  onChange={(e) => {
                    setCls(e.target.value);
                    setSubcls("");
                  }}
                  MenuProps={MenuProps}
                >
                  <MenuItem value="">None</MenuItem>
                  {clsName.map((name) => (
                    <MenuItem
                      sx={DropdownSx}
                      key={name.ClassName}
                      value={name.ClassName}
                    >
                      {name.ClassName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {[groupBy[2], groupBy[3], groupBy[5]].includes(applyDisc) && (
            <Grid item xs={12} sm={4} md={2.4} lg={2.4}>
              <FormControl
                size="small"
                fullWidth
                margin="dense"
                error={
                  ([groupBy[3]].includes(applyDisc) && !!deptNameError) ||
                  ([groupBy[5]].includes(applyDisc) && !!vendorNameError)
                }
              >
                <InputLabel id="subCls">Planogram</InputLabel>
                <Select
                  labelId="subCls"
                  label="Planogram"
                  value={subcls}
                  onChange={(e) => {
                    setSubcls(e.target.value);
                  }}
                  MenuProps={MenuProps}
                >
                  <MenuItem value="">None</MenuItem>
                  {subName.map((name) => (
                    <MenuItem
                      sx={DropdownSx}
                      key={name.SubClassName}
                      value={name.SubClassName}
                    >
                      {name.SubClassName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {applyDisc === groupBy[3] && (
            <Grid item xs={12} sm={4} md={2.4} lg={2.4}>
              <Tooltip title="Enter Planogram Name (Max 100 characters)" arrow>
                <TextField
                  fullWidth
                  margin="dense"
                  size="small"
                  label="Planogram #/Name"
                  error={[groupBy[3]].includes(applyDisc) && !!deptNameError}
                  inputProps={{ maxLength: 100 }}
                  {...register("planogram")}
                />
              </Tooltip>
            </Grid>
          )}
          {applyDisc === groupBy[4] && (
            <Grid item xs={12} sm={4} md={2.4} lg={2.4}>
              <Tooltip title="Enter 6 digit SKU number" arrow>
                <TextField
                  fullWidth
                  required
                  margin="dense"
                  size="small"
                  label="SKU"
                  inputProps={{ maxLength: 6 }}
                  onKeyDown={restrictAlpha}
                  {...register("sku", {
                    required: "This field is required.",
                    pattern: {
                      value: /\b[1-9][0-9][0-9][0-9][0-9][0-9]\b/,
                      message: "Please enter 6 digit number",
                    },
                  })}
                  error={!!errors?.sku}
                  helperText={errors?.sku ? errors.sku.message : null}
                />
              </Tooltip>
            </Grid>
          )}
          {applyDisc === groupBy[5] && (
            <Grid item xs={12} sm={4} md={2.4} lg={2.4}>
              <Tooltip title="Enter vendor name" arrow>
                <TextField
                  fullWidth
                  margin="dense"
                  size="small"
                  label="Vendor"
                  {...register("vendor")}
                  error={!!vendorNameError}
                />
              </Tooltip>
            </Grid>
          )}
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={12}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            {[groupBy[0], groupBy[3], groupBy[5]].includes(applyDisc) && (
              <Chip
                icon={
                  <InfoIcon
                    sx={{
                      color: (theme) =>
                        `${
                          !!deptNameError || !!vendorNameError
                            ? theme.palette.error.main
                            : grey[500]
                        } !important`,
                    }}
                  />
                }
                size="small"
                variant="outlined"
                label={
                  applyDisc === groupBy[0]
                    ? "Note: It's recommended to select one department"
                    : "Note: Please select any one field/combination from above four fields"
                }
                sx={{
                  alignSelf: "center",
                  justifySelf: "flex-start",
                  color: (theme) =>
                    !!deptNameError || !!vendorNameError
                      ? theme.palette.error.main
                      : grey[500],
                  borderColor: (theme) =>
                    !!deptNameError || !!vendorNameError
                      ? theme.palette.error.main
                      : grey[500],
                }}
              />
            )}
            <Button
              variant="outlined"
              sx={{ marginLeft: "auto", mr: 2 }}
              onClick={onReset}
              data-testid="reset-button"
            >
              Reset
            </Button>
            <Button
              variant="contained"
              type="submit"
              data-testid="search-button"
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>
      {promiseInProgress && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </form>
  );
}

export default withSnackbar(DiscountSearch);
